import React from "react";
import { Button, Placeholder } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCopy } from "@fortawesome/free-solid-svg-icons";
import BillingAlertActionsMenu from "./BillingAlertActionsMenu";
import { formatDate, resolvePriorityTone } from "./billingAlertsUtils";

const PRIORITY_LABELS = {
  critical: "Crítica",
  high: "Alta",
  medium: "Media",
  low: "Baja",
};


export default function BillingAlertCard({
  item,
  expanded = false,
  detail = null,
  detailLoading = false,
  detailError = "",
  copied = false,
  onToggle,
  onManage,
  onTake,
  onAssign,
  onRelease,
  onCopyMessage,
}) {
  const detailData = detail || {};
  const priorityTone = resolvePriorityTone(item.priority);
  const metaItems = [
    { key: "cuit", label: "CUIT", value: item.cuit },
    { key: "terminal", label: "Terminal", value: item.terminal },
    { key: "province", label: "Provincia", value: item.province },
    { key: "phone", label: "Teléfono", value: item.phone },
  ].filter((meta) => meta.value);
  const pattern = detailData.pattern || item.pattern;
  const secondaryTag = detailData.segment || item.segment || detailData.classification || item.classification;
  const detailTags = [pattern, secondaryTag].filter((tag, index, tags) => tag && tags.indexOf(tag) === index);
  const metricBadges = detailData.metricBadges || [];
  const suggestedMessage = detailData.suggestedMessage || item.suggestedMessage;

  return (
    <article className={`billing-alert-card ${expanded ? "is-expanded" : ""}`}>
      <div className="billing-alert-card__summary">
        <button
          type="button"
          className="billing-alert-card__identity"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <div className="billing-alert-card__main">
            <strong className="billing-alert-card__commerce">{item.commerceName}</strong>
            {metaItems.length ? (
              <div className="billing-alert-card__meta">
                {metaItems.map((meta) => (
                  <span key={meta.key} className="billing-alert-card__meta-item">
                    <b>{meta.label}:</b> {meta.value}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </button>

        <div className="billing-alert-card__controls" onClick={(event) => event.stopPropagation()}>
          <div className="billing-alert-card__badges">
            <span className="billing-alert-card__pill">{item.status}</span>
            <span className={`billing-alert-card__advance ${item.advanceActive.toLowerCase() === "activo" ? "is-active" : "is-inactive"}`}>
              {item.advanceActive.toLowerCase() === "activo" ? "Activo" : "Inactivo"}
            </span>
            {item.score !== null ? <span className="billing-alert-card__pill">Score {item.score}</span> : null}
            {item.priority ? <span className={`billing-alert-card__priority is-${priorityTone}`}>{PRIORITY_LABELS[priorityTone] || item.priority}</span> : null}
          </div>

          <BillingAlertActionsMenu
            item={item}
            onManage={onManage}
            onTake={onTake}
            onAssign={onAssign}
            onRelease={onRelease}
            onCopyMessage={onCopyMessage}
          />

          <button
            type="button"
            className="billing-alert-card__toggle"
            onClick={onToggle}
            aria-label={expanded ? "Contraer alerta de facturación" : "Expandir alerta de facturación"}
            aria-expanded={expanded}
          >
            <FontAwesomeIcon icon={faChevronDown} className={expanded ? "is-open" : ""} />
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="billing-alert-card__detail">
          {detailLoading ? (
            <div className="billing-alert-card__detail-loading">
              <Placeholder as="div" animation="glow"><Placeholder xs={12} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={10} /></Placeholder>
              <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
            </div>
          ) : detailError ? (
            <div className="billing-alert-card__detail-error">
              <span>{detailError}</span>
            </div>
          ) : (
            <>
              <div className="billing-alert-card__detail-heading">
                <h4>{detailData.mainReason || item.mainReason || "Alerta de facturación activa"}</h4>
                {metricBadges.length ? (
                  <div className="billing-alert-card__metrics">
                    {metricBadges.map((metric) => (
                      <span key={metric.key} className="billing-alert-card__metric-pill">
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="billing-alert-card__detail-column">
                <div className="billing-alert-card__detail-lines">
                  {detailData.suggestedAction || item.suggestedAction ? (
                    <p><b>Acción sugerida:</b> {detailData.suggestedAction || item.suggestedAction}</p>
                  ) : null}
                  {pattern ? (
                    <p><b>Patrón detectado:</b> {pattern}</p>
                  ) : null}
                  <div className="billing-alert-card__contact-row">
                    {detailData.lastContactAt || item.lastContactAt ? (
                      <p><b>Último contacto:</b> {formatDate(detailData.lastContactAt || item.lastContactAt) || detailData.lastContactAt || item.lastContactAt}</p>
                    ) : null}
                    {detailData.nextContactAt || item.nextContactAt ? (
                      <p><b>Próximo contacto:</b> {formatDate(detailData.nextContactAt || item.nextContactAt) || detailData.nextContactAt || item.nextContactAt}</p>
                    ) : null}
                  </div>
                  {detailData.responsibleName || item.responsibleName ? (
                    <p><b>Responsable:</b> {detailData.responsibleName || item.responsibleName}</p>
                  ) : null}
                </div>

                {detailTags.length ? (
                  <div className="billing-alert-card__detail-mini-badges">
                    {detailTags.map((tag) => (
                      <span key={tag} className="billing-alert-card__pill">{tag}</span>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="billing-alert-card__detail-column is-message">
                {suggestedMessage ? (
                  <>
                    <p className="billing-alert-card__message-text">
                      <b className="billing-alert-card__message-label">Mensaje sugerido:</b>{" "}
                      <span>“{suggestedMessage}”</span>
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      className="billing-alert-card__copy"
                      onClick={() => onCopyMessage(suggestedMessage)}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      <span>{copied ? "Mensaje copiado" : "Copiar"}</span>
                    </Button>
                  </>
                ) : (
                  <p className="billing-alert-card__message-empty">
                    <b className="billing-alert-card__message-label">Mensaje sugerido:</b> Sin mensaje disponible.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}
